import { Component } from '@theme/component';

/** Approved menu interaction; native header, drawer and commerce controls retain ownership. */
class SofaDecorHeaderNavigation extends Component {
  controller;
  current = null;
  openTimer;
  closeTimer;
  focusTimer;
  drawerObserver;

  connectedCallback() {
    super.connectedCallback();
    this.setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.setup();
  }

  disconnectedCallback() {
    this.cleanup();
    super.disconnectedCallback();
  }

  cleanup() {
    this.close();
    clearTimeout(this.focusTimer);
    this.drawerObserver?.disconnect();
    this.controller?.abort();
  }

  close(restore = false) {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    const group = this.current;
    this.current = null;
    if (!group) return;
    const button = group.querySelector('.sd-nav-trigger');
    button?.setAttribute('aria-expanded', 'false');
    const panel = group.querySelector('.sd-mega-panel');
    if (panel) panel.hidden = true;
    if (restore && button?.isConnected) button.focus();
  }

  open(group, focus = false) {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    if (this.current !== group) {
      this.close();
      this.current = group;
      group.querySelector('.sd-nav-trigger')?.setAttribute('aria-expanded', 'true');
      const panel = group.querySelector('.sd-mega-panel');
      if (panel) panel.hidden = false;
    }
    if (focus) group.querySelector('.sd-mega-panel a')?.focus();
  }

  setup() {
    this.cleanup();
    this.controller = new AbortController();
    const signal = this.controller.signal;
    const on = (target, type, listener, options = {}) => target?.addEventListener(type, listener, { ...options, signal });
    if (this.dataset.variant === 'mobile') {
      const drawer = this.closest('header-drawer');
      const details = drawer?.querySelector(':scope > details');
      const summary = details?.querySelector(':scope > summary');
      if (details && summary) {
        let wasOpen = details.open;
        const sync = () => {
          const restore = wasOpen && !details.open && (details.contains(document.activeElement) || document.activeElement === document.body);
          summary.setAttribute('aria-expanded', String(details.open));
          summary.setAttribute('aria-label', details.open ? this.dataset.closeLabel : this.dataset.openLabel);
          wasOpen = details.open;
          if (restore && summary.getClientRects().length) summary.focus({ preventScroll: true });
        };
        on(details, 'toggle', sync);
        this.drawerObserver = new MutationObserver(sync);
        this.drawerObserver.observe(details, { attributes: true, attributeFilter: ['open'] });
        sync();
      }
      const groups = [...this.querySelectorAll('.sd-mobile-group')];
      for (const group of groups) {
        on(group, 'toggle', () => {
          group.querySelector('summary')?.setAttribute('aria-expanded', String(group.open));
          if (group.open) for (const other of groups) if (other !== group) other.open = false;
        });
        on(group, 'keydown', event => {
          if (event.key !== 'Escape') return;
          event.preventDefault();
          event.stopPropagation();
          if (group.open) {
            group.open = false;
            group.querySelector('summary')?.focus();
          } else {
            this.closest('header-drawer')?.close();
          }
        });
        // Keep the native drawer's keyup handler from re-closing the nested group.
        on(group, 'keyup', event => { if (event.key === 'Escape') event.stopPropagation(); });
      }
      on(document, 'click', event => {
        if (!(event.target instanceof Element) || !event.target.closest('#header-group button[name="Search"]')) return;
        if (drawer?.isOpen) drawer.close();
      }, { capture: true });
      const desktop = matchMedia('(min-width:1200px)');
      const closeDesktopDrawer = () => { if (desktop.matches && drawer?.isOpen) drawer.close(); };
      on(desktop, 'change', closeDesktopDrawer);
      closeDesktopDrawer();
      return;
    }
    const groups = [...this.querySelectorAll('.sd-nav-group')];
    const buttons = groups.map(group => group.querySelector('.sd-nav-trigger'));
    for (const group of groups) {
      const button = group.querySelector('.sd-nav-trigger');
      on(button, 'click', () => this.current === group ? this.close() : this.open(group));
      on(button, 'keydown', event => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          this.open(group, true);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          const rtl = getComputedStyle(this).direction === 'rtl' ? -1 : 1;
          const direction = (event.key === 'ArrowLeft' ? -1 : 1) * rtl;
          this.close();
          buttons[(buttons.indexOf(button) + direction + buttons.length) % buttons.length]?.focus();
        }
      });
      on(group, 'pointerenter', event => {
        if (event.pointerType !== 'mouse' || !matchMedia('(hover:hover)').matches) return;
        clearTimeout(this.closeTimer);
        clearTimeout(this.openTimer);
        this.openTimer = setTimeout(() => this.open(group), 100);
      });
      on(group, 'pointerleave', () => {
        clearTimeout(this.openTimer);
        if (!group.contains(document.activeElement)) this.closeTimer = setTimeout(() => this.close(), 180);
      });
    }
    on(this, 'focusout', () => {
      clearTimeout(this.focusTimer);
      this.focusTimer = setTimeout(() => {
        if (this.current && !this.current.contains(document.activeElement)) this.close();
      }, 0);
    });
    on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.close(); });
    on(document, 'keydown', event => {
      if (event.key === 'Escape' && this.current) {
        event.preventDefault();
        this.close(true);
      }
    });
    on(matchMedia('(min-width:1200px)'), 'change', () => this.close());
  }
}

if (!customElements.get('sd-header-navigation')) customElements.define('sd-header-navigation', SofaDecorHeaderNavigation);
